/**
 * Карточка превью, которая СЛЕДУЕТ ЗА ТЕКСТОМ. docs/telegram.md §5 (ПРАВИЛА 5.1, 5.2, 5.2а).
 *
 * Одно правило на два пути записи — сервис генерации (`services/agent-writer/pipeline/build.ts`)
 * и правку человеком (`services/backend/domain/human-edit.ts`): адрес карточки — первая
 * `text_link` по положению в тексте, и он задаётся Telegram явно. Явный `url` — единственная
 * рабочая форма (ПРАВИЛО 5.2): без него сервер игнорирует `prefer_large_media`, а
 * предусловие A7 канонизации отбивает `prefer_*` при пустом `url` как fatal.
 *
 * Ссылки под текстом нет — опции остаются дефолтом Telegram без `prefer_*`: карточка не
 * запрещена, и голый адрес с путём (`geoboost.ru/blog`) сервер подхватит сам своим
 * `get_first_url`. Детектор голых ссылок здесь НЕ вызывается намеренно: `url`-сущности в
 * `MessageEntity` нет (её ставит сервер), а порт детектора живёт во входе `preview` и в
 * решения о содержимом не допускается (§Хребет 3.5). То, что сервер выбрал сам, сверка
 * §6 читает как заметку, а не как расхождение.
 *
 * Почему правка человеком не наследует опции правимой версии (решение Ильи 2026-09-01):
 * редактор добавлял ссылку в текст и не получал карточки — версия несла `is_disabled: true`
 * от формата или прежнего прогона, а кнопок управления карточкой в фазе 1 нет. Явно
 * присланные опции (`link_preview_options` в теле правки) по-прежнему сильнее этого правила.
 */

import type { LinkPreviewOptions, MessageEntity } from './draft.ts';

/** Первая `text_link` по offset — не по индексу: порядок чужого массива ничего не обещает. */
export function firstTextLinkUrl(entities: readonly MessageEntity[]): string | null {
  let first: { offset: number; url: string } | null = null;
  for (const e of entities) {
    if (e.type !== 'text_link') continue;
    if (first === null || e.offset < first.offset) first = { offset: e.offset, url: e.url };
  }
  return first === null ? null : first.url;
}

/**
 * Опции карточки по сущностям текста: ссылка есть — рабочая форма ПРАВИЛА 5.2 с явным
 * адресом и крупной картинкой; ссылки нет — дефолт Telegram.
 *
 * Карточка ПОД текстом (`show_above_text: false`) — решение Ильи 2026-09-01 по живым постам:
 * над текстом она отодвигала лид вниз экрана, а пост открывается лидом.
 */
export function linkPreviewFollowingText(entities: readonly MessageEntity[]): LinkPreviewOptions {
  const url = firstTextLinkUrl(entities);
  if (url === null) {
    return {
      is_disabled: false,
      url: null,
      prefer_small_media: false,
      prefer_large_media: false,
      show_above_text: false,
    };
  }
  return {
    is_disabled: false,
    url,
    prefer_small_media: false,
    prefer_large_media: true,
    show_above_text: false,
  };
}
